import { Selection, IObjectWithKey, SelectionMode } from "@fluentui/react";
import { useEffect, useMemo, useState } from "react";

/**
 * ALIASES TEMPORÁRIOS PARA TIPAGEM
 * TODO: Substituir cada alias abaixo por tipos concretos quando o débito técnico for tratado.
 */

type IItems = { items?: Array<IItem> };
interface IAllCurrentItems {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items?: Array<any>;
}
interface IItem {
  id: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

type IItemsArray = Array<IItem>;

interface UsePersistentSelectionParams {
  items: IItems;
  allCurrentItems: IAllCurrentItems;
  isLoadingList: boolean;
  selectionMode: SelectionMode;
  canBeMultipleSelection?: boolean;
  isCheckSelection?: boolean;
  setSelectedItem?: (selectedItem: IItem) => void;
}

interface UsePersistentSelectionResult {
  selection: Selection<IObjectWithKey>;
  itemsSelected: IItemsArray;
  setItemsSelected: React.Dispatch<React.SetStateAction<IItemsArray>>;
}

export function usePersistentSelection({
  items,
  allCurrentItems,
  isLoadingList,
  selectionMode,
  canBeMultipleSelection,
  isCheckSelection,
  setSelectedItem
}: UsePersistentSelectionParams): UsePersistentSelectionResult {
  const [itemsSelected, setItemsSelected] = useState<IItemsArray>([]);
  const [listDataSelectionListener, setListDataSelectionListener] =
    useState<IItemsArray>();

  const selection = useMemo(() => {
    return new Selection({
      onSelectionChanged: () => {
        if (setSelectedItem && isCheckSelection) {
          const selected = selection.getSelection() as IItemsArray;
          setListDataSelectionListener(selected);
        }
      }
    });
  }, [setSelectedItem, isCheckSelection]);

  useEffect(() => {
    if (!listDataSelectionListener) return;

    const hasCurrentSelection = listDataSelectionListener.length > 0;

    if (hasCurrentSelection) {
      const isMultiple =
        selectionMode === SelectionMode.multiple || canBeMultipleSelection;

      if (isMultiple) {
        let mergedSelection: IItemsArray = listDataSelectionListener;

        for (const previouslySelected of itemsSelected.filter(Boolean)) {
          const existsOnCurrentPage = items?.items?.find(
            (pageItem: IItem) => pageItem.id === previouslySelected.id
          );

          const stillExplicitlySelected = mergedSelection.find(
            (pageItem: IItem) => pageItem?.id === previouslySelected?.id
          );

          if (existsOnCurrentPage) {
            if (!stillExplicitlySelected) {
              mergedSelection = mergedSelection.filter(
                (pageItem: IItem) => pageItem.id !== previouslySelected.id
              );
            }
          } else {
            if (!stillExplicitlySelected && previouslySelected) {
              mergedSelection.push(previouslySelected);
            }
          }
        }

        setItemsSelected(mergedSelection);
      } else {
        if (setSelectedItem) {
          setSelectedItem(listDataSelectionListener[0]);
        }
      }

      return;
    }

    if (
      allCurrentItems?.items &&
      itemsSelected &&
      itemsSelected[0] &&
      allCurrentItems?.items[0]
    ) {
      const stillSelectedOutsidePage = itemsSelected.filter(
        previouslySelected =>
          !allCurrentItems?.items?.find(
            (pageItem: IItem) => pageItem.id === previouslySelected.id
          )
      );

      if (setSelectedItem) {
        setSelectedItem(undefined);
      }
      setItemsSelected(stillSelectedOutsidePage);
    }
  }, [
    allCurrentItems?.items,
    canBeMultipleSelection,
    items?.items,
    itemsSelected,
    listDataSelectionListener,
    selectionMode,
    setSelectedItem
  ]);

  useEffect(() => {
    if (
      !isLoadingList &&
      allCurrentItems?.items &&
      allCurrentItems?.items[0] &&
      itemsSelected &&
      itemsSelected[0]
    ) {
      itemsSelected.forEach(selectedItem => {
        const itemsOnPage = allCurrentItems?.items;

        if (!Array.isArray(itemsOnPage)) return;

        const indexOnPage = itemsOnPage.findIndex(
          (pageItem: IItem) => pageItem?.id === selectedItem?.id
        );

        if (indexOnPage >= 0) {
          selection.setIndexSelected(indexOnPage, true, true);
        }
      });
    }
  }, [isLoadingList, itemsSelected, allCurrentItems, selection]);

  return {
    selection,
    itemsSelected,
    setItemsSelected
  };
}
